/* eslint-disable max-lines-per-function */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { TableUtility } from '@utility/dynamic-table';
import { toast } from 'react-toastify';
import { MODULE_NAMES } from '../../../Constants/modules';
import { AuthContext } from '../../../Contexts/AuthContext';
import FormDialog from '../../../Components/Modal/modal';
import UseAxios from '../../../Api/http/http';
import { getResourceTypes } from '../../../Api/request/projectDetails';
import MultiSelectAutocomplete from '../../../Components/MultiSelectAutocomplete/multiSelectAutocomplete';
import {
  createEmployeeExperience,
  getEmployeeExperiences,
  updateEmployeeExperience,
} from '../../../Api/request/employeeExperience';
import ControlledSwitches from '../../../Components/Switch/switch';
import EditActionIcon from '../../../Assets/images/svg/dar_edit.svg';
import alertIcon from '../../../Assets/images/svg/alert_icon.svg';
import { employeeExperienceTableConfig } from './employeeExperienceTableConfig';
import './employeeExperience.scss';

const EMPTY_FORM = { experienceRange: '', roleIds: [] };
const EMPTY_PERMISSIONS = [];

const getApiErrorMessage = (response) => {
  const payload = response?.data;
  if (payload instanceof Error) {
    return payload?.response?.data?.message
      || payload?.response?.data?.externalMessage
      || payload.message
      || 'Something went wrong.';
  }
  if (payload?.status === false) {
    return payload?.message
      || payload?.response?.data?.message
      || payload?.response?.data?.externalMessage
      || 'Something went wrong.';
  }
  return response?.data?.response?.data?.message
    || response?.data?.message
    || response?.data?.response?.data?.externalMessage
    || '';
};

const normalizeEmployeeExperience = (experience) => ({
  id: experience.id,
  range: experience.experienceRange || experience.experience_range || '',
  isActive: Boolean(experience.isActive ?? experience.is_active),
  roleIds: (experience.roleIds || []).map(Number),
});

const normalizeEmployeeExperiences = (response) => {
  const rows = Array.isArray(response?.data)
    ? response.data
    : response?.data?.employeeExperienceList || response?.data?.data || [];

  return rows.map((experience) => normalizeEmployeeExperience(experience));
};

const EmployeeExperienceForm = ({
  mode,
  form,
  error,
  isSubmitDisabled,
  onChange,
  onClose,
  onSubmit,
  roleOptions,
  rolesLoading,
  onRolesChange,
}) => createPortal(
  <div className="employee-experience-modal-backdrop">
    <form className="employee-experience-modal" onSubmit={onSubmit}>
      <div className="employee-experience-modal__header">
        {mode === 'edit' ? 'Edit Employee Experience' : 'Add Employee Experience'}
      </div>
      <div className="employee-experience-modal__body">
        <label htmlFor="employee-experience-range" className={error ? 'is-error' : ''}>
          Experience Range<span className="required-asterisk">*</span>
        </label>
        <input
          id="employee-experience-range"
          name="experienceRange"
          type="text"
          placeholder="Enter Experience Range"
          value={form.experienceRange}
          onChange={onChange}
          className={error ? 'is-error' : ''}
          autoComplete="off"
        />
        <div className="employee-experience-modal__roles">
          <MultiSelectAutocomplete
            label="Employee Role"
            placeholder="Select Employee Roles"
            options={roleOptions}
            value={roleOptions.filter((role) => form.roleIds.includes(role.id))}
            onChange={onRolesChange}
            disabled={rolesLoading || !roleOptions.length}
            multiSelectCss="employee-experience-role-select"
          />
        </div>
      </div>
      <div className="employee-experience-modal__footer">
        <button
          type="button"
          className="employee-experience-modal__btn employee-experience-modal__btn--ghost"
          onClick={onClose}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="employee-experience-modal__btn employee-experience-modal__btn--primary"
          disabled={isSubmitDisabled}
        >
          {mode === 'edit' ? 'Update' : 'Add'}
        </button>
      </div>
    </form>
  </div>,
  document.body,
);

const EmployeeExperience = () => {
  const { userProfile } = React.useContext(AuthContext);
  const permission = userProfile?.permissions?.[MODULE_NAMES.CONFIGURATION_EMPLOYEE_EXPERIENCE];
  const isSuperAdmin = userProfile?.roleDetails?.some(
    (role) => role.roleName === 'Super Admin',
  );
  const normalizedPermissions = useMemo(
    () => (permission || EMPTY_PERMISSIONS).map((action) => String(action).toLowerCase()),
    [permission],
  );
  const hasAccess = useCallback((action) => {
    if (isSuperAdmin) return true;
    const actionAliases = {
      read: ['read', 'view'],
      create: ['create', 'add'],
      update: ['update', 'edit'],
    };
    return actionAliases[action]?.some((alias) => normalizedPermissions.includes(alias)) || false;
  }, [isSuperAdmin, normalizedPermissions]);

  const [employeeExperiences, setEmployeeExperiences] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [dialog, setDialog] = useState({ isOpen: false, mode: 'add', id: null });
  const [toggleEmployeeExperience, setToggleEmployeeExperience] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [hasError, setHasError] = useState(false);
  const [roleOptions, setRoleOptions] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [rolesLoaded, setRolesLoaded] = useState(false);

  useEffect(() => {
    if (!dialog.isOpen) return;
    setRolesLoaded(false);
    UseAxios(getResourceTypes({}, Date.now()), setRolesLoading).then((response) => {
      const errorMessage = getApiErrorMessage(response);
      if (errorMessage) {
        toast.error(errorMessage);
        return;
      }
      const roles = response?.data?.resourceTypeList
        || response?.data?.data?.resourceTypeList
        || response?.data?.data
        || [];
      setRoleOptions(roles.map((role) => ({ id: Number(role.type_id), name: role.resource_type })));
      setRolesLoaded(true);
    });
  }, [dialog.isOpen]);

  const loadEmployeeExperiences = useCallback(async () => {
    if (!hasAccess('read')) return;
    const response = await UseAxios(getEmployeeExperiences(), setIsLoading);
    const errorMessage = getApiErrorMessage(response);
    if (errorMessage) {
      toast.error(errorMessage);
      return;
    }
    setEmployeeExperiences(normalizeEmployeeExperiences(response));
  }, [hasAccess]);

  useEffect(() => {
    loadEmployeeExperiences();
  }, [loadEmployeeExperiences]);

  const closeForm = useCallback(() => {
    setDialog({ isOpen: false, mode: 'add', id: null });
    setForm(EMPTY_FORM);
    setHasError(false);
  }, []);

  const openAdd = useCallback(() => {
    if (!hasAccess('create')) return;
    setRolesLoaded(false);
    setDialog({ isOpen: true, mode: 'add', id: null });
    setForm(EMPTY_FORM);
    setHasError(false);
  }, [hasAccess]);

  const openEdit = useCallback((employeeExperience) => {
    if (!hasAccess('update')) return;
    setRolesLoaded(false);
    setDialog({ isOpen: true, mode: 'edit', id: employeeExperience.id });
    setForm({ experienceRange: employeeExperience.range, roleIds: employeeExperience.roleIds });
    setHasError(false);
  }, [hasAccess]);

  const selectedEmployeeExperience = employeeExperiences.find(
    (experience) => experience.id === dialog.id,
  );
  const isSubmitDisabled = isLoading || !rolesLoaded || rolesLoading || !form.experienceRange.trim()
    || (
      dialog.mode === 'edit'
      && form.experienceRange.trim() === selectedEmployeeExperience?.range.trim()
      && form.roleIds.length === selectedEmployeeExperience?.roleIds.length
      && form.roleIds.every((id) => selectedEmployeeExperience.roleIds.includes(id))
    );

  const handleSubmit = useCallback(async (event) => {
    event.preventDefault();
    if (isSubmitDisabled) return;
    if (!hasAccess(dialog.mode === 'edit' ? 'update' : 'create')) return;
    const experienceRange = form.experienceRange.trim();
    if (!experienceRange) {
      setHasError(true);
      toast.error('Please enter an experience range.');
      return;
    }

    const updateRequest = updateEmployeeExperience(
      dialog.id,
      experienceRange,
      selectedEmployeeExperience?.isActive ?? true,
      form.roleIds,
    );
    const response = dialog.mode === 'edit'
      ? await UseAxios(updateRequest, setIsLoading)
      : await UseAxios(createEmployeeExperience(experienceRange, form.roleIds), setIsLoading);
    const errorMessage = getApiErrorMessage(response);
    if (errorMessage) {
      toast.error(errorMessage);
      return;
    }
    toast.success(dialog.mode === 'edit'
      ? 'Employee experience updated successfully.'
      : 'Employee experience added successfully.');

    const savedEmployeeExperience = normalizeEmployeeExperience(
      response?.data?.employeeExperience || response?.data?.data || response.data,
    );
    setEmployeeExperiences((currentExperiences) => (
      dialog.mode === 'edit'
        ? currentExperiences.map((experience) => (
          experience.id === savedEmployeeExperience.id ? savedEmployeeExperience : experience
        ))
        : [...currentExperiences, savedEmployeeExperience]
    ));
    closeForm();
  }, [closeForm, dialog.id, dialog.mode, form, hasAccess, selectedEmployeeExperience, isSubmitDisabled]);

  const handleToggle = useCallback((employeeExperience) => {
    if (!hasAccess('update')) return;
    setToggleEmployeeExperience(employeeExperience);
  }, [hasAccess]);

  const handleCloseToggleDialog = useCallback(() => {
    setToggleEmployeeExperience(null);
  }, []);

  const handleConfirmToggle = useCallback(async () => {
    if (!toggleEmployeeExperience || !hasAccess('update')) return;

    const nextStatus = !toggleEmployeeExperience.isActive;
    const response = await UseAxios(updateEmployeeExperience(
      toggleEmployeeExperience.id,
      toggleEmployeeExperience.range,
      nextStatus,
    ), setIsLoading);
    const errorMessage = getApiErrorMessage(response);
    if (errorMessage) {
      toast.error(errorMessage);
      return;
    }

    setEmployeeExperiences((currentExperiences) => currentExperiences.map((experience) => (
      experience.id === toggleEmployeeExperience.id
        ? { ...experience, isActive: nextStatus }
        : experience
    )));
    toast.success(`${toggleEmployeeExperience.range} is now ${nextStatus ? 'active' : 'inactive'}.`);
    handleCloseToggleDialog();
  }, [handleCloseToggleDialog, hasAccess, toggleEmployeeExperience]);

  const tableData = useMemo(() => employeeExperiences.map((experience, index) => ({
    ...experience,
    slNo: index + 1,
    experienceRange: experience.range,
    status: experience.isActive ? 'Active' : 'Inactive',
    action: (
      <div className="employee-experience-actions" onClick={(event) => event.stopPropagation()}>
        <ControlledSwitches
          checked={experience.isActive}
          disabled={!hasAccess('update')}
          switchCss="switchCss"
          handleChange={() => handleToggle(experience)}
        />
        <button
          type="button"
          className="employee-experience-action-btn"
          title="Edit Employee Experience"
          aria-label={`Edit ${experience.range}`}
          disabled={!hasAccess('update')}
          onClick={() => openEdit(experience)}
        >
          <img src={EditActionIcon} alt="" />
        </button>
      </div>
    ),
  })), [employeeExperiences, handleToggle, hasAccess, openEdit]);

  const configuration = useMemo(() => ({
    generalSettings: {
      enableApiFiltering: false,
      showPagination: false,
      showToolBar: true,
      userInfo: {},
      handlers: { handleAddIconClick: openAdd },
      toolBarSettings: {
        showAddNew: hasAccess('create'),
        showFilterOption: false,
        showDownloadExcel: false,
        showDownloadCsv: false,
        showExpandCollapse: false,
        showColumnCustomize: false,
        showHelp: false,
        showSearch: false,
        showSaveView: false,
        showReset: false,
        showToolBarHeading: true,
        toolBarHeading: 'Employee Experience',
      },
    },
    showToolBar: true,
    tableSettings: {
      size: 'small',
      enableTreeTable: false,
      showExpandCollapse: false,
      tableConfig: employeeExperienceTableConfig,
      loaderSkeleton: { rows: 8, bodyCells: 1, columns: employeeExperienceTableConfig.length },
      paginationConfig: { page: 1, limit: tableData.length, total: tableData.length, totalPages: 1 },
      tableScrollHeight: 'calc(100vh - 140px)',
      maxFrozenColumns: { desktop: 1, tablet: 1, mobile: 1 },
    },
  }), [hasAccess, openAdd, tableData.length]);

  if (!hasAccess('read')) {
    return <div>You do not have permission to view employee experiences.</div>;
  }

  return (
    <div className="employee-experience-tu">
      <TableUtility configuration={configuration} data={tableData} loading={isLoading} />
      {dialog.isOpen && (
        <EmployeeExperienceForm
          mode={dialog.mode}
          form={form}
          error={hasError}
          isSubmitDisabled={isSubmitDisabled}
          roleOptions={roleOptions}
          rolesLoading={rolesLoading}
          onRolesChange={(event, roles) => setForm((current) => ({
            ...current, roleIds: roles.map((role) => role.id),
          }))}
          onChange={(event) => {
            setForm((current) => ({ ...current, experienceRange: event.target.value }));
            setHasError(false);
          }}
          onClose={closeForm}
          onSubmit={handleSubmit}
        />
      )}
      <FormDialog
        open={Boolean(toggleEmployeeExperience)}
        title={`${toggleEmployeeExperience?.isActive ? 'Disable' : 'Enable'} Employee Experience`}
        dialogCss="dialog-message"
        dialogPaperCss="client-enable-dialog-paper"
        paperprops={{}}
        dialogBodycontent={(
          <div className="dialog-body-text">
            <div className="dialog-alert-icon">
              <img alt="Alert Icon" className="icon-image" src={alertIcon} />
            </div>
            <div className="dialog-message-text">
              {`Are you sure you want to ${
                toggleEmployeeExperience?.isActive ? 'disable' : 'enable'
              } this employee experience?`}
            </div>
          </div>
        )}
        button1="Cancel"
        cancelButtonId="cancel-employee-experience-toggle"
        button2={toggleEmployeeExperience?.isActive ? 'Disable' : 'Enable'}
        submitButtonId="confirm-employee-experience-toggle"
        button1Action={handleCloseToggleDialog}
        button2Action={handleConfirmToggle}
      />
    </div>
  );
};

export default EmployeeExperience;
